import styled, { css } from "styled-components";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import {
  V2_INPUT_HEIGHT_PX,
  V2_INPUT_WIDTH_PX,
} from "components/designV2/constants";

export type DropdownState = "inactive" | "active" | "filled";

export const Container = styled.div`
  position: relative;
  width: ${V2_INPUT_WIDTH_PX}px;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const Label = styled.span`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue4};
`;

export const FieldWrapper = styled.div<{ $state: DropdownState }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: ${V2_INPUT_HEIGHT_PX}px;
  padding: 0 16px;
  border-radius: 16px;
  background-color: ${ColorV2.White};
  border: 1.5px solid ${ColorV2.Blue5};
  transition: border-color 0.2s ease;

  ${(props) =>
    props.$state === "active" &&
    css`
      border-color: ${ColorV2.Blue1};
    `}
`;

export const Input = styled.input`
  ${TypographyV2.b2}
  flex: 1 1 0;
  min-width: 0;
  border: none;
  background: transparent;
  outline: none;
  color: ${ColorV2.Blue1};

  &::placeholder {
    color: ${ColorV2.Blue5};
  }
`;

export const IconWrapper = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
`;

export const ActionSlot = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

export const SelectionBadge = styled.span`
  ${TypographyV2.s1}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  border-radius: 10px;
  background: ${ColorV2.Blue7};
  color: ${ColorV2.Blue4};
  white-space: nowrap;
`;

export const TooltipList = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.$columns}, minmax(0, 1fr));
  column-gap: 16px;
  row-gap: 6px;
`;

export const TooltipColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const TooltipItem = styled.span`
  ${TypographyV2.s1}
  color: inherit;
  white-space: nowrap;
`;

export const TooltipAligner = styled.span`
  display: inline-flex;
  align-self: center;
`;

export const ClearButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
`;

export const List = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin: 4px 0 0 0;
  padding: 8px 0;
  border-radius: 16px;
  background-color: ${ColorV2.White};
  border: 1.5px solid ${ColorV2.Blue5};
  box-shadow: 0 12px 24px rgba(15, 42, 64, 0.12);
  max-height: 260px;
  overflow: hidden;
  z-index: 1000;
  display: ${(props) => (props.$isOpen ? "block" : "none")};
`;

export const ListScroll = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 260px;
  overflow-y: scroll;
  padding: 16px 12px 18px;
  box-sizing: border-box;

  scrollbar-gutter: stable both-edges;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${ColorV2.Blue7};
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${ColorV2.Blue6};
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${ColorV2.Blue5};
  }
`;

export const UnitBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 12px;
`;

export const EntityBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 12px;
`;

export const UnitsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 28px;

  ${UnitBlock} {
    padding-bottom: 4px;
  }
`;

export const TeamsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 28px;
`;

export const OptionRow = styled.div`
  display: flex;
  align-items: center;

  & > label {
    width: 100%;
  }
`;

export const EmptyState = styled.div`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue4};
  padding: 8px 12px 16px;
`;
